import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { fetchWorkoutTemplate } from "@/api/workoutTemplateApi";
import {
  buildWorkoutPrescriptionSections,
  formatPrescriptionMethod,
  formatTotalWorkoutDuration,
  sessionHasStructuredPrescription,
} from "@/lib/workoutPrescriptionFormatters";
import { SessionPrescriptionView } from "@/components/coaching/SessionPrescriptionView";

const StepBlock = ({ item }) => (
  <div className="rounded-md border border-dark-border/70 bg-dark-surface px-3 py-2">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <div className="text-sm text-dark-text-primary">
        {item.headline || item.label}
      </div>
      {item.duration && (
        <div className="text-xs text-dark-text-muted">{item.duration}</div>
      )}
    </div>
    {item.intensity && (
      <div className="mt-1 text-xs text-dark-text-secondary">{item.intensity}</div>
    )}
    {item.cadence && (
      <div className="mt-1 text-xs text-dark-text-secondary">
        Cadence: {item.cadence}
      </div>
    )}
    {item.stroke && (
      <div className="mt-1 text-xs text-dark-text-secondary">{item.stroke}</div>
    )}
    {item.cue && (
      <div className="mt-1 text-xs text-dark-text-muted whitespace-pre-wrap">
        {item.cue}
      </div>
    )}
    {item.substeps?.length > 0 && (
      <div className="mt-2 space-y-2 border-l border-dark-border pl-3">
        {item.substeps.map((sub, index) => (
          <StepBlock key={`${sub.label}-${index}`} item={sub} />
        ))}
      </div>
    )}
  </div>
);

export const WorkoutPrescriptionDetail = ({
  session,
  fetchTemplate = fetchWorkoutTemplate,
}) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasInlinePrescription = sessionHasStructuredPrescription(session);
  const workoutCode = session?.source_template_id
    || session?.workout_code
    || session?.workout_name;
  const templateVersion = session?.source_template_version;

  useEffect(() => {
    if (hasInlinePrescription || !workoutCode) {
      setTemplate(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchTemplate({
      workoutCode,
      templateVersion,
      signal: controller.signal,
    })
      .then((data) => setTemplate(data))
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setTemplate(null);
        setError("Detailed workout prescription is unavailable.");
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [fetchTemplate, hasInlinePrescription, templateVersion, workoutCode]);

  if (hasInlinePrescription) {
    return (
      <div className="mt-3" data-testid="workout-prescription-detail">
        <SessionPrescriptionView session={session} />
      </div>
    );
  }

  const sections = buildWorkoutPrescriptionSections(template);
  const total = formatTotalWorkoutDuration(template, session?.duration_minutes);

  if (loading) {
    return (
      <div
        className="mt-3 flex items-center gap-2 text-sm text-dark-text-muted"
        data-testid="workout-prescription-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        Loading workout details…
      </div>
    );
  }

  if (error) {
    return (
      <p className="mt-3 text-sm text-dark-text-muted" data-testid="workout-prescription-error">
        {error}
      </p>
    );
  }

  if (!sections.length) {
    return (
      <div className="mt-3 text-sm text-dark-text-secondary space-y-1"
            data-testid="workout-prescription-fallback">
        {session?.prescription_method && (
          <p>{formatPrescriptionMethod(session.prescription_method)}</p>
        )}
        {session?.intensity_target_summary && (
          <p>Target: {session.intensity_target_summary}</p>
        )}
        {session?.duration_minutes != null && (
          <p>Total: {session.duration_minutes} min</p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-4" data-testid="workout-prescription-detail">
      {sections.map((section) => (
        <div key={section.key}>
          <div className="text-[11px] uppercase tracking-[0.25em] text-dark-text-muted">
            {section.title}
          </div>
          <div className="mt-2 space-y-2">
            {section.items.map((item, index) => (
              <StepBlock key={`${section.key}-${index}`} item={item} />
            ))}
          </div>
        </div>
      ))}
      {total && (
        <div className="text-xs uppercase tracking-widest text-dark-text-muted">
          Total · {total}
        </div>
      )}
    </div>
  );
};
